import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, Mail, MapPin, Shield, BadgeCheck, ChevronLeft
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getRepAssociateDetail, removeRepresentativeToken } from '../services/representativeService';
import AssociativeAppShell from '../components/AssociativeAppShell';

interface AssociateDetail {
  _id: string;
  codice: string;
  stato: string;
  categoria: string;
  integrativa?: string;
  integrativeArray?: string[];
  nominativo: string;
  partitaIVA: string;
  codiceFiscale?: string;
  email: string;
  emailSecondarie?: string[];
  pec?: string;
  telefono: string;
  telefoniExtra?: string[];
  indirizzo?: string;
  civico?: string;
  cap?: string;
  citta: string;
  provincia: string;
  regione: string;
  iscrizione?: string;
  statoTesseramento?: string;
  tipoPagamento?: string;
  fineTesseramento?: string;
  rappresentante?: string;
  rappresentanteNome?: string;
  rappresentanteCognome?: string;
  certifiedNetworkStatus?: {
    isInNetwork: boolean;
    status: string | null;
    certificateStatus: string | null;
    isActive: boolean;
  };
}

/* Accento blu/indigo di sezione "Associati" (la shell resta verde). */
const ACCENT = '#2456A8';

/* Coppia label/valore — presentazionale, nessuna logica. */
const Detail: React.FC<{ label: string; mono?: boolean; full?: boolean; children: React.ReactNode }> = ({ label, mono, full, children }) => (
  <div style={full ? { gridColumn: '1 / -1' } : undefined}>
    <div className="mono-label">{label}</div>
    <div style={{ marginTop: '4px', font: `500 14px var(--font-${mono ? 'mono' : 'body'})`, color: 'var(--text-strong)', wordBreak: 'break-word' }}>
      {children}
    </div>
  </div>
);

const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '18px 24px' };
const grid1: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr', gap: '16px' };

const AssociativeAssociateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [associate, setAssociate] = useState<AssociateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }

    if (!id) return;

    const load = async () => {
      try {
        const res = await getRepAssociateDetail(id);
        if (res.success) {
          setAssociate(res.data);
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        if (err.response?.status === 403) {
          setError('Non hai accesso a questo associato');
        } else if (err.response?.status === 404) {
          setError('Associato non trovato');
        } else {
          setError('Errore nel caricamento');
          toast.error('Errore caricamento dettaglio associato');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate, toast]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return dateStr; }
  };

  const getCategoriaBadge = (categoria: string) => {
    const colors: Record<string, string> = {
      'STUDIO': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'AUTOSCUOLA': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'NAUTICA': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
    };
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors[categoria] || 'bg-gray-100 text-gray-800'}`}>{categoria}</span>;
  };

  const getStatoBadge = (stato: string) => {
    const colors: Record<string, string> = {
      'ASSOCIATO': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'FUTURO ASSOCIATO': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'IN ISCRIZIONE': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'IN RECESSO': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors[stato] || 'bg-gray-100 text-gray-800'}`}>{stato}</span>;
  };

  const getTesseramentoBadge = (tess?: string) => {
    if (!tess) return <span className="text-xs text-gray-400">N/D</span>;
    const colors: Record<string, string> = {
      'PAGATO': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'ATTIVATO': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'IN ATTIVAZIONE': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'NON PAGATO': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors[tess] || 'bg-gray-100 text-gray-800'}`}>{tess}</span>;
  };

  if (loading) {
    return (
      <AssociativeAppShell route="associati" associatesAccess={true}>
        <div className="page">
          <div className="muted" style={{ textAlign: 'center', padding: '80px 0', fontSize: '13px' }}>Caricamento…</div>
        </div>
      </AssociativeAppShell>
    );
  }

  if (error || !associate) {
    return (
      <AssociativeAppShell route="associati" associatesAccess={true}>
        <div className="page fade-in">
          <button type="button" className="back-btn" onClick={() => navigate('/area-associativa/associati')}>
            <ChevronLeft size={14} /> Torna alla lista
          </button>
          <div className="muted" style={{ textAlign: 'center', padding: '64px 0', fontSize: '13px' }}>
            {error || 'Associato non trovato'}
          </div>
        </div>
      </AssociativeAppShell>
    );
  }

  const integrative = associate.integrativeArray && associate.integrativeArray.length > 0
    ? associate.integrativeArray
    : associate.integrativa ? associate.integrativa.split(/\s+/).filter(Boolean) : [];

  const fullAddress = [associate.indirizzo, associate.civico].filter(Boolean).join(' ');
  const fullCity = [associate.cap, associate.citta].filter(Boolean).join(' ');
  const cns = associate.certifiedNetworkStatus;

  return (
    <AssociativeAppShell route="associati" associatesAccess={true}>
      <div className="page fade-in">
        {/* Header */}
        <button type="button" className="back-btn" onClick={() => navigate('/area-associativa/associati')}>
          <ChevronLeft size={14} /> Torna alla lista
        </button>
        <div style={{ margin: '8px 0 26px' }}>
          <h1 style={{ margin: 0, font: '700 25px/1.2 var(--font-display)', letterSpacing: 'var(--tracking-tight)', color: 'var(--text-strong)' }}>
            {associate.nominativo}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            <span className="mono-label" style={{ fontSize: '12px' }}>{associate.codice}</span>
            {getCategoriaBadge(associate.categoria)}
            {getStatoBadge(associate.stato)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Dati Impresa */}
          <section className="card">
            <div className="card-head">
              <Building2 size={16} style={{ color: ACCENT }} />
              <h2>Dati Impresa</h2>
            </div>
            <div className="card-body" style={grid2}>
              <Detail label="Nominativo" full>{associate.nominativo}</Detail>
              <Detail label="P.IVA" mono>{associate.partitaIVA}</Detail>
              {associate.codiceFiscale && (
                <Detail label="Codice Fiscale" mono>{associate.codiceFiscale}</Detail>
              )}
              <Detail label="Codice Associato" mono>{associate.codice}</Detail>
              <Detail label="Categoria" full>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {getCategoriaBadge(associate.categoria)}
                  {integrative.map((item, idx) => (
                    <span key={idx} className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      item === 'STUDIO' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                      item === 'AUTOSCUOLA' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' :
                      item === 'NAUTICA' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-white/60 dark:bg-black/20 text-[8px] font-bold leading-none">i</span>
                      {item}
                    </span>
                  ))}
                </div>
              </Detail>
            </div>
          </section>

          {/* Contatti */}
          <section className="card">
            <div className="card-head">
              <Mail size={16} style={{ color: ACCENT }} />
              <h2>Contatti</h2>
            </div>
            <div className="card-body" style={grid1}>
              <Detail label="Email">{associate.email || 'N/D'}</Detail>
              {associate.emailSecondarie && associate.emailSecondarie.length > 0 && (
                <Detail label="Email secondarie">{associate.emailSecondarie.join(', ')}</Detail>
              )}
              {associate.pec && (
                <Detail label="PEC">{associate.pec}</Detail>
              )}
              <Detail label="Telefono">
                {associate.telefono || 'N/D'}
                {associate.telefoniExtra && associate.telefoniExtra.length > 0 && (
                  <span style={{ display: 'block', font: '400 12.5px var(--font-body)', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {associate.telefoniExtra.join(', ')}
                  </span>
                )}
              </Detail>
            </div>
          </section>

          {/* Sede */}
          <section className="card">
            <div className="card-head">
              <MapPin size={16} style={{ color: ACCENT }} />
              <h2>Sede</h2>
            </div>
            <div className="card-body" style={grid2}>
              {fullAddress && (
                <Detail label="Indirizzo" full>{fullAddress}</Detail>
              )}
              {fullCity && (
                <Detail label="Città">{fullCity}</Detail>
              )}
              <Detail label="Provincia">{associate.provincia}</Detail>
              <Detail label="Regione">{associate.regione}</Detail>
            </div>
          </section>

          {/* Stato Associativo */}
          <section className="card">
            <div className="card-head">
              <Shield size={16} style={{ color: ACCENT }} />
              <h2>Stato Associativo</h2>
            </div>
            <div className="card-body" style={grid2}>
              <Detail label="Stato">{getStatoBadge(associate.stato)}</Detail>
              <Detail label="Tesseramento">{getTesseramentoBadge(associate.statoTesseramento)}</Detail>
              {associate.tipoPagamento && associate.tipoPagamento !== 'NON SPECIFICATO' && (
                <Detail label="Tipo Pagamento">
                  <span style={{ textTransform: 'capitalize' }}>{associate.tipoPagamento}</span>
                </Detail>
              )}
              {associate.iscrizione && (
                <Detail label="Data Iscrizione">{formatDate(associate.iscrizione)}</Detail>
              )}
              {associate.fineTesseramento && (
                <Detail label="Scadenza Tesseramento">{formatDate(associate.fineTesseramento)}</Detail>
              )}
              {(associate.rappresentanteNome || associate.rappresentanteCognome || associate.rappresentante) && (
                <Detail label="Rappresentante Associativo" full>
                  {(associate.rappresentanteNome || associate.rappresentanteCognome)
                    ? `${associate.rappresentanteCognome || ''} ${associate.rappresentanteNome || ''}`.trim()
                    : associate.rappresentante || '-'}
                </Detail>
              )}
            </div>
          </section>

          {/* Rete Certificata — full width */}
          <section className="card lg:col-span-2">
            <div className="card-head">
              <BadgeCheck size={16} style={{ color: ACCENT }} />
              <h2>Rete Certificata</h2>
            </div>
            <div className="card-body">
              {cns?.isInNetwork ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
                  <span className={`badge ${cns.isActive ? 's-inviato' : 't-provinciale'}`}>
                    <Shield size={13} /> Iscritto
                  </span>
                  {cns.status && (
                    <Detail label="Stato Membro">
                      <span style={{ textTransform: 'capitalize' }}>{cns.status}</span>
                    </Detail>
                  )}
                  {cns.certificateStatus && (
                    <Detail label="Stato Certificato">
                      <span className={`badge ${
                        cns.certificateStatus === 'ATTIVO' ? 's-inviato'
                        : cns.certificateStatus === 'IN ATTESA' ? 't-provinciale'
                        : 's-bozza'
                      }`}>
                        {cns.certificateStatus}
                      </span>
                    </Detail>
                  )}
                </div>
              ) : (
                <div className="muted" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13.5px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--un-ink-300)', flex: '0 0 auto' }} />
                  Non iscritto alla Rete Certificata
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </AssociativeAppShell>
  );
};

export default AssociativeAssociateDetail;
